import React, { useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil'
import styled from '@emotion/styled'
import { PersonalIcon } from 'src/components/insights/icons/personal'
import { PlusCircle } from '@emotion-icons/fa-solid/PlusCircle'
import { Shapes } from 'src/components/theme'
import { Insight } from 'src/components/insights/types'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { InsightBuilderToolbar } from 'src/views/insight-builder/insight-toolbar'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { useResetInsightBuilder } from 'src/views/insight-builder/use-reset-insight-builder'
import { CustomInsight } from 'src/types/insight-builder'
import InsightLocked from 'src/components/insight-locked'
import { INSIGHT_PAGE_CLASS } from 'src/components/insights/page'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { INSIGHT_ICON_MAPPING } from 'src/views/insight-builder/insight-icon-selector'
import { useTier } from 'src/hooks/use-tier'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'

import * as insightBuilderAtoms from 'src/atoms/insight-builder'

const InsightBuilderContent = dynamic(() => import('src/views/insight-builder/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

export const InsightBuilderLocked = styled.div`
  height: 100%;
  width: 100%;
  padding: ${Shapes.spacing[6]}px;
  overflow-x: hidden;
  overflow-y: scroll;
  overflow-y: overlay;
  @media (max-width: 870px) {
    padding-inline: ${Shapes.spacing[6]}px;
  }

  ${FANCY_SCROLLBAR}
`

const InsightBuilderPageContent = () => {
  const [selectedUserInsightId, setSelectedUserInsightId] = useRecoilState(insightBuilderAtoms.selectedCustomInsightId)
  const customInsights = useRecoilValueLoadable(insightBuilderAtoms.customInsights)
  const setSelectedWidgetsData = useSetRecoilState(insightBuilderAtoms.selectedWidgetsData)
  const setInsightTitle = useSetRecoilState(insightBuilderAtoms.insightTitle)
  const setInsightIcon = useSetRecoilState(insightBuilderAtoms.insightIcon)
  const router = useRouter()
  const { query } = useParsedRouter()
  const resetInsightBuilder = useResetInsightBuilder()
  const [cachedSelectedUserInsightId, setCachedSelectedUserInsightId] = useState<string | undefined>(undefined)
  const locked = !useTier(INSIGHTS_TIER['Custom Reports'])

  const hasCustomInsights = useMemo(() => {
    if (customInsights.state === 'hasValue') {
      const insights = customInsights.contents as CustomInsight[]
      return insights.length > 0
    }
  }, [customInsights])

  useEffect(() => {
    // Whenver the query changes, load the insight if possible else go back to home
    const loadInsights = async () => {
      const insights = customInsights.contents as CustomInsight[]

      // If the insight is already selected, do nothing
      if (query.iid === cachedSelectedUserInsightId) {
        if (customInsights.state !== 'hasValue') {
          return
        }
        const cachedInsight = insights.find((insight) => insight.id === cachedSelectedUserInsightId)
        if (!cachedInsight) {
          return
        }
        setSelectedUserInsightId(cachedInsight.id)
        setInsightTitle(cachedInsight.title)
        setInsightIcon(cachedInsight.icon)
        setSelectedWidgetsData(cachedInsight.widgetsData)
        return
      }
      // If the custom insights are not loaded, do nothing
      if (customInsights.state !== 'hasValue') {
        return
      }

      // no user insight found, go back to home
      const insight = insights.find((insight) => insight.id === query.iid)
      if (!insight) {
        const queryToReturnTo = query['id.am-loc']
          ? `?${'id.am-loc'}=${query['id.am-loc']}`
          : query['id.google']
            ? `?${'id.google'}=${query['id.google']}`
            : ''
        await router.push(`/${queryToReturnTo}`)
        return
      }
      // Set the insight id + widget ids + title + icon
      setSelectedUserInsightId(insight.id)
      setCachedSelectedUserInsightId(insight.id)
      setInsightTitle(insight.title)
      setInsightIcon(insight.icon)
      setSelectedWidgetsData(insight.widgetsData)
    }
    void loadInsights()
  }, [query, selectedUserInsightId])

  useEffect(() => {
    return () => resetInsightBuilder()
  }, [])

  if ((locked && !query.iid) || (locked && !hasCustomInsights)) {
    return (
      <InsightBuilderLocked>
        <div className={INSIGHT_PAGE_CLASS}>
          <InsightLocked
            insight={{
              id: 'insight-builder',
              component: () => null,
              name: 'Custom Reports',
              icon: PlusCircle,
              supportedResultTypes: ['alphamap-location', 'address', 'area'],
              linkTooltipFile: '',
              paywall: {
                title: 'Create Custom Reports',
                description: `Custom reports isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
                synopsis: [
                  {
                    image: 'https://placehold.co/350x200',
                    imagePlaceholder: 'https://placehold.co/350x200',
                    title: 'Choose Custom Widgets',
                    description:
                      'Choose your own set of widgets from the different categories to build reports as you see fit.',
                  },
                  {
                    image: 'https://placehold.co/350x200',
                    imagePlaceholder: 'https://placehold.co/350x200',
                    title: 'Add Custom Maps',
                    description:
                      'Want to support your selected widgets with custom-made maps? Add maps from Map Builder in your own custom reports.',
                  },
                  {
                    image: 'https://placehold.co/350x200',
                    imagePlaceholder: 'https://placehold.co/350x200',
                    title: 'Add Branding & Export',
                    description:
                      "Finally, share your custom reports to anyone you wish: export and download PDFs. Would you like to add custom branding? That's possible!.",
                  },
                ],
              },
            }}
          />
        </div>
      </InsightBuilderLocked>
    )
  }

  if (locked && hasCustomInsights) {
    return <LockedInsight />
  }

  return (
    <>
      <InsightBuilderToolbar />
      <InsightBuilderContent />
    </>
  )
}

const LockedInsight = () => {
  const insightTitle = useRecoilValue(insightBuilderAtoms.insightTitle)
  const insightIcon = useRecoilValue(insightBuilderAtoms.insightIcon)
  const icon = INSIGHT_ICON_MAPPING[insightIcon]
  return (
    <InsightBuilderLocked>
      <div className={INSIGHT_PAGE_CLASS}>
        <InsightLocked
          insight={{
            id: 'insight-builder',
            component: () => null,
            name: insightTitle,
            icon,
            supportedResultTypes: ['alphamap-location', 'address', 'area'],
            linkTooltipFile: '',
            paywall: {
              title: 'Unlock Your Custom Reports',
              description: `Custom reports aren't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
              synopsis: [
                {
                  image: 'https://placehold.co/350x200',
                  imagePlaceholder: 'https://placehold.co/350x200',
                  title: 'View Your Custom Reports Again',
                  description:
                    'Choose your own set of widgets from the different categories to build reports as you see fit.',
                },
                {
                  image: 'https://placehold.co/350x200',
                  imagePlaceholder: 'https://placehold.co/350x200',
                  title: 'Add Custom Maps',
                  description:
                    'Want to support your selected widgets with custom-made maps? Add maps from Map Builder in your own custom reports.',
                },
                {
                  image: 'https://placehold.co/350x200',
                  imagePlaceholder: 'https://placehold.co/350x200',
                  title: 'Add Branding & Export',
                  description:
                    "Finally, share your custom reports to anyone you wish: export and download PDFs. Would you like to add custom branding? That's possible!.",
                },
              ],
            },
          }}
        />
      </div>
    </InsightBuilderLocked>
  )
}

const InsightBuilderPage = () => {
  return (
    <React.Suspense fallback={<FullScreenLoading />}>
      <InsightBuilderPageContent />
    </React.Suspense>
  )
}

export const InsightBuilder: Insight = {
  id: 'insight-builder',
  component: InsightBuilderPage,
  name: 'Personal Reports',
  icon: PersonalIcon,
  supportedResultTypes: ['alphamap-location', 'address', 'area'],
  linkTooltipFile: '',
}
